import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { User, Lock, Save, CheckCircle, AlertCircle } from "lucide-react";

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current: "", new: "", confirm: "" });
  const [profileMsg, setProfileMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileMsg(null);
    try {
      await api.put("/auth/profile", { name });
      updateUser({ name });
      setProfileMsg({ type: "success", text: "Profile updated successfully" });
    } catch (err) {
      setProfileMsg({ type: "error", text: formatError(err) });
    } finally {
      setLoading(false);
    }
  };

  const handlePwChange = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw.new !== pw.confirm) return setPwMsg({ type: "error", text: "New passwords do not match" });
    if (pw.new.length < 6) return setPwMsg({ type: "error", text: "Password must be at least 6 characters" });
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.new });
      setPwMsg({ type: "success", text: "Password changed successfully" });
      setPw({ current: "", new: "", confirm: "" });
    } catch (err) {
      setPwMsg({ type: "error", text: formatError(err) });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Account</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900">Profile & Settings</h1>
        </div>

        {/* Profile section */}
        <div className="bg-white border border-[#E2E8E3] rounded-md p-6 mb-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sage-100 rounded-md flex items-center justify-center">
              <User className="w-5 h-5 text-sage-600" />
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-sage-900">Personal Information</h3>
              <p className="text-sage-500 text-xs">{user?.email}</p>
            </div>
          </div>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Full Name</label>
              <input
                data-testid="profile-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Email Address</label>
              <input type="email" value={user?.email || ""} disabled className="w-full border border-sage-100 rounded-md px-3 py-2 text-sm bg-sage-50 text-sage-400 cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Role</label>
              <input type="text" value={user?.role || ""} disabled className="w-full border border-sage-100 rounded-md px-3 py-2 text-sm bg-sage-50 text-sage-400 cursor-not-allowed capitalize" />
            </div>
            {profileMsg && (
              <div className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${profileMsg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {profileMsg.type === "success" ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                {profileMsg.text}
              </div>
            )}
            <button
              data-testid="profile-save-button"
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        {/* Password section */}
        <div className="bg-white border border-[#E2E8E3] rounded-md p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-50 rounded-md flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-sage-900">Change Password</h3>
              <p className="text-sage-500 text-xs">Use a strong password at least 6 characters</p>
            </div>
          </div>
          <form onSubmit={handlePwChange} className="space-y-4">
            {[
              { label: "Current Password", key: "current", testId: "current-password-input" },
              { label: "New Password", key: "new", testId: "new-password-input" },
              { label: "Confirm New Password", key: "confirm", testId: "confirm-password-input" },
            ].map(({ label, key, testId }) => (
              <div key={key}>
                <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">{label}</label>
                <input
                  data-testid={testId}
                  type="password"
                  value={pw[key]}
                  onChange={(e) => setPw({ ...pw, [key]: e.target.value })}
                  className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none"
                  required
                />
              </div>
            ))}
            {pwMsg && (
              <div className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${pwMsg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {pwMsg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {pwMsg.text}
              </div>
            )}
            <button
              data-testid="change-password-button"
              type="submit"
              disabled={pwLoading}
              className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-60"
            >
              <Lock className="w-4 h-4" />
              {pwLoading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
